import { Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';

import Logo from 'src/components/logo';
import { useResponsive } from 'src/hooks/useResponsive';
import { AuthContext } from 'src/auth/context';
import { mobileNavItems } from 'src/layouts/main/config-navigation';
import NavMobile from 'src/layouts/main/nav-mobile';
import NetworkStatusBanner from 'src/components/banner-switch-network/NetworkStatusBanner';
import WalletConnectButtonClient from 'src/components/wallet-connect/WalletConnectButton-client';
import { useSelector } from 'react-redux';
import { LiquidityBar } from 'src/components/liquidity-bar/LiquidityBar';
import AccountMenu from 'src/features/avatar/accountMenu';
import { lazyRetry } from 'src/utils/lazyRetry';

const loadLoginModal = () => import('src/features/auth/loginModal');

const LoginModal = lazyRetry(loadLoginModal);

const NAV_ITEMS = [
  { label: 'Swap', to: '/' },
  { label: 'Send USD', to: '/send-usd' },
  { label: 'To Exchange', to: '/to-exchange' },
  { label: 'Transactions', to: '/transactions' },
  { label: 'New Design', to: '/new-design' },
];

function Header() {
  const theme = useTheme();
  const location = useLocation();

  const [openLogin, setOpenLogin] = useState(false);
  const [hasOpenedLogin, setHasOpenedLogin] = useState(false);
  const [loginModalKey, setLoginModalKey] = useState(0);

  const { user, isUserAuthenticated, loading, prepareAuthRequests } = useContext(AuthContext);

  const mdUp = useResponsive('up', 'md');

  const usd = useSelector((state) => state.contractBalance?.usd);
  const usdt = useSelector((state) => state.contractBalance?.usdt);

  const isSendUsdPage = location.pathname.startsWith('/send-usd');
  const isTransactionsPage = location.pathname.startsWith('/transactions');
  const isSwapPage = location.pathname === '/';

  const authType = user?.authType || null;

  const isPhoneSession = Boolean(isUserAuthenticated && authType === 'phone');
  const isWalletSession = Boolean(isUserAuthenticated && authType === 'wallet');

  const shouldShowPhoneLoginButton = useMemo(() => {
    if (loading) return false;

    // Full phone user already logged in.
    if (isPhoneSession) return false;

    // Swap requires phone login.
    if (isSwapPage) return true;

    // Transactions can work with wallet, but login is still useful
    // if no wallet session exists.
    if (isTransactionsPage && !isWalletSession) return true;

    // Send USD only needs wallet.
    if (isSendUsdPage) return false;

    return true;
  }, [loading, isPhoneSession, isWalletSession, isSwapPage, isTransactionsPage, isSendUsdPage]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  const preloadLogin = useCallback(() => {
    loadLoginModal().catch(() => null);
  }, []);

  const handleOpenLogin = async () => {
    setLoginModalKey((current) => current + 1);
    setHasOpenedLogin(true);
    setOpenLogin(true);

    await prepareAuthRequests?.();
  };

  const handleCloseLogin = useCallback(() => {
    setOpenLogin(false);
  }, []);

  const renderLoginAction = () => {
    if (loading) {
      return <Box sx={{ width: 40, height: 40 }} />;
    }

    // Full phone-authenticated user.
    // This is the only case where AccountMenu should show.
    if (isPhoneSession) {
      return <AccountMenu />;
    }

    // Wallet-only session.
    // Do NOT show AccountMenu because that looks like full user login.
    if (isWalletSession) {
      if (isSwapPage) {
        return (
          <Button
            onClick={handleOpenLogin}
            onMouseEnter={preloadLogin}
            onFocus={preloadLogin}
            variant="contained"
            sx={{
              minWidth: { xs: 76, md: 96 },
              height: 40,
              px: { xs: 1.5, md: 2 },
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 14,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            Login
          </Button>
        );
      }

      return (
        <Chip
          label="Wallet session"
          size="small"
          variant="outlined"
          sx={{
            height: 34,
            borderRadius: 2,
            fontWeight: 800,
            color: 'text.secondary',
            borderColor: alpha(theme.palette.text.primary, 0.18),
            bgcolor: alpha(theme.palette.text.primary, 0.035),
            display: { xs: 'none', sm: 'inline-flex' },
          }}
        />
      );
    }

    if (!shouldShowPhoneLoginButton) {
      return null;
    }

    return (
      <Button
        onClick={handleOpenLogin}
        onMouseEnter={preloadLogin}
        onFocus={preloadLogin}
        variant="contained"
        sx={{
          minWidth: { xs: 76, md: 96 },
          height: 40,
          px: { xs: 1.5, md: 2 },
          borderRadius: 2.5,
          textTransform: 'none',
          fontWeight: 800,
          fontSize: 14,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }}
      >
        Login
      </Button>
    );
  };

  const renderRightActions = () => {
    return (
      <>
        <WalletConnectButtonClient />
        {renderLoginAction()}
      </>
    );
  };

  return (
    <>
      {hasOpenedLogin && (
        <Suspense
          fallback={
            openLogin ? (
              <Box
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: theme.zIndex.modal,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.common.black, 0.35),
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Box
                  sx={{
                    width: 112,
                    height: 48,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow:
                      theme.palette.mode === 'light'
                        ? '0 16px 40px rgba(16, 24, 40, 0.18)'
                        : '0 16px 40px rgba(0, 0, 0, 0.45)',
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </Box>
            ) : null
          }
        >
          <LoginModal key={loginModalKey} open={openLogin} onClose={handleCloseLogin} />
        </Suspense>
      )}

      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          width: '100%',
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow:
            theme.palette.mode === 'light'
              ? '0 1px 2px rgba(16, 24, 40, 0.04)'
              : '0 1px 2px rgba(0, 0, 0, 0.24)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1280,
            mx: 'auto',
            px: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 64, md: 72 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={{ xs: 1.25, md: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  lineHeight: 0,
                }}
              >
                <Logo />
              </Box>

              {mdUp ? (
                <Stack direction="row" spacing={0.5} sx={{ ml: 0.5 }}>
                  {NAV_ITEMS.map((item) => {
                    const active = isActive(item.to);

                    return (
                      <Box
                        key={item.to}
                        component={Link}
                        to={item.to}
                        sx={{
                          position: 'relative',
                          px: 1.5,
                          py: 1.15,
                          borderRadius: 2.5,
                          textDecoration: 'none',
                          color: active ? 'text.primary' : 'text.secondary',
                          fontWeight: active ? 800 : 700,
                          fontSize: 14,
                          letterSpacing: 0.1,
                          bgcolor: active
                            ? alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.14 : 0.08
                              )
                            : 'transparent',
                          transition: theme.transitions.create(
                            ['background-color', 'color', 'transform'],
                            { duration: theme.transitions.duration.shorter }
                          ),
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 12,
                            right: 12,
                            bottom: 7,
                            height: 2,
                            borderRadius: 99,
                            bgcolor: active ? 'primary.main' : 'transparent',
                            transition: theme.transitions.create(['background-color']),
                          },
                          '&:hover': {
                            color: 'text.primary',
                            bgcolor: active
                              ? alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === 'dark' ? 0.18 : 0.12
                                )
                              : alpha(
                                  theme.palette.text.primary,
                                  theme.palette.mode === 'dark' ? 0.08 : 0.04
                                ),
                          },
                        }}
                      >
                        {item.label}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <NavMobile data={mobileNavItems} />
              )}
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={{ xs: 0.75, md: 1 }}
              sx={{
                flexShrink: 0,
              }}
            >
              {renderRightActions()}
            </Stack>
          </Box>

          <NetworkStatusBanner />
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 0, md: 3 },
        }}
      >
        <LiquidityBar usd={usd} usdt={usdt} />
      </Box>
    </>
  );
}

export default Header;
